/**
 * Hook for loading reference data needed by the parsing workflow:
 * projects, queries/regions, active Yandex credentials, and AI config.
 *
 * Extracted from useParsingWorkflow.ts (Phase 2.2 refactoring).
 *
 * Owns the loaded data + loading flags. Auto-fill of selectedQueries /
 * selectedRegionCodes after `loadQueriesRegions` is delegated back to the
 * facade through `onQueriesRegionsLoaded` so the facade keeps ownership
 * of user-controlled selection state.
 */

import { useCallback, useState, type Dispatch, type SetStateAction } from 'react'
import { message } from 'antd'
import api from '@/services/api'
import type { AIConfig, QueriesRegionsData, ResolvedRegion } from '../types'

export interface DataLoadersProject {
  id: number
  crm_id: number
  project_name: string
  status: string
}

interface UseDataLoadersProps {
  projectId?: number | null
  projectCrmId?: number | null
  projectName?: string | null
  /** Callback called once queries/regions are loaded so the facade can
   *  auto-fill its selectedQueries / selectedRegionCodes state. */
  onQueriesRegionsLoaded?: (args: {
    queries: string[]
    resolvedRegionCodes: string[]
  }) => void
}

export interface UseDataLoadersResult {
  projects: DataLoadersProject[]
  loadingProjects: boolean
  queriesRegions: QueriesRegionsData | null
  loadingQueriesRegions: boolean
  resolvedRegions: ResolvedRegion[]
  autoCredentialsId: number | null
  aiConfig: AIConfig | null
  loadingAIConfig: boolean
  setAIConfig: Dispatch<SetStateAction<AIConfig | null>>
  loadProjects: () => Promise<void>
  loadQueriesRegions: (currentProjectId: number) => Promise<void>
  loadActiveCredentials: () => Promise<void>
  loadAIConfig: () => Promise<void>
  /** Reset all loaded data (used by resetState/handleStartOver in the facade) */
  resetData: () => void
}

export function useDataLoaders({
  projectId,
  projectCrmId,
  projectName,
  onQueriesRegionsLoaded,
}: UseDataLoadersProps): UseDataLoadersResult {
  const [projects, setProjects] = useState<DataLoadersProject[]>([])
  const [loadingProjects, setLoadingProjects] = useState(false)

  const [queriesRegions, setQueriesRegions] = useState<QueriesRegionsData | null>(null)
  const [loadingQueriesRegions, setLoadingQueriesRegions] = useState(false)
  const [resolvedRegions, setResolvedRegions] = useState<ResolvedRegion[]>([])

  const [autoCredentialsId, setAutoCredentialsId] = useState<number | null>(null)

  const [aiConfig, setAIConfig] = useState<AIConfig | null>(null)
  const [loadingAIConfig, setLoadingAIConfig] = useState(false)

  const loadProjects = useCallback(async () => {
    try {
      setLoadingProjects(true)
      const data = await api.getProjects({
        page: 1,
        page_size: 1000,
        parse_manual: true,
      })
      setProjects(data.items || data.projects || [])
    } catch (error) {
      message.error('Ошибка загрузки проектов')
      console.error(error)
    } finally {
      setLoadingProjects(false)
    }
  }, [])

  const loadQueriesRegions = useCallback(
    async (currentProjectId: number) => {
      try {
        setLoadingQueriesRegions(true)
        const data = await api.getProjectQueriesRegions(currentProjectId)
        setQueriesRegions(data)

        const queries: string[] = data.queries && data.queries.length > 0 ? data.queries : []
        let resolvedRegionCodes: string[] = []

        if (data.regions && data.regions.length > 0) {
          try {
            const { resolveRegions } = await import('../../../utils/yandex-regions')
            const resolved = resolveRegions(data.regions) as ResolvedRegion[]
            setResolvedRegions(resolved)

            resolvedRegionCodes = resolved
              .filter((r) => r.success && r.yandex_id)
              .map((r) => r.yandex_id!.toString())

            const successCount = resolved.filter((r) => r.success).length
            if (successCount > 0) {
              message.success(
                `Регионы успешно преобразованы (${successCount}/${resolved.length})`
              )
            }
          } catch (error) {
            console.error('Failed to resolve regions:', error)
            setResolvedRegions([])
          }
        }

        onQueriesRegionsLoaded?.({ queries, resolvedRegionCodes })
      } catch (error) {
        console.error('Failed to load queries and regions:', error)
        setQueriesRegions(null)
        setResolvedRegions([])
      } finally {
        setLoadingQueriesRegions(false)
      }
    },
    [onQueriesRegionsLoaded]
  )

  const loadActiveCredentials = useCallback(async () => {
    try {
      const { getCredentialsList } = await import('../../../services/yandexAuthService')
      const credentialsData = await getCredentialsList()
      const validCredentials = credentialsData.items
        .filter((c) => c.is_active && c.is_session_valid && c.is_crc_token_valid)
        .sort((a, b) => (a.crc_token_age_hours || 999) - (b.crc_token_age_hours || 999))
      const activeCredentials = validCredentials[0]

      if (activeCredentials) {
        setAutoCredentialsId(activeCredentials.id)
      } else {
        message.warning('Нет валидных Yandex credentials')
      }
    } catch (error) {
      console.error('Error loading credentials:', error)
    }
  }, [])

  const loadAIConfig = useCallback(async () => {
    const crmId = projectCrmId || projectId
    if (!crmId) return

    try {
      setLoadingAIConfig(true)

      // Add timeout to prevent page hang (15 seconds)
      const timeout = (ms: number) =>
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Request timeout')), ms)
        )

      const [config, queriesRegionsData] = (await Promise.all([
        Promise.race([api.getProjectAIConfig(crmId), timeout(15000)]),
        Promise.race([api.getProjectQueriesRegions(crmId), timeout(15000)]),
      ])) as [AIConfig, QueriesRegionsData]

      const enrichedConfig: AIConfig = {
        ...config,
        project_name: projectName || undefined,
        queries: queriesRegionsData.queries || [],
        regions:
          queriesRegionsData.regions?.map((r) => String(r.name || r.id || '')) || [],
      }

      setAIConfig(enrichedConfig)
      message.success('AI конфигурация загружена')
    } catch (error: unknown) {
      console.error('Failed to load AI config:', error)
      const isTimeout =
        error instanceof Error && error.message === 'Request timeout'
      if (isTimeout) {
        message.warning('Таймаут загрузки AI конфигурации')
      } else {
        message.warning('Не удалось загрузить AI конфигурацию')
      }

      // Use fallback config from already-loaded queriesRegions (if any)
      setAIConfig({
        project_name: projectName || undefined,
        queries: queriesRegions?.queries || [],
        regions:
          queriesRegions?.regions?.map((r) => String(r.name || r.id || '')) || [],
      })
    } finally {
      setLoadingAIConfig(false)
    }
  }, [projectId, projectCrmId, projectName, queriesRegions])

  const resetData = useCallback(() => {
    setQueriesRegions(null)
    setResolvedRegions([])
    setLoadingQueriesRegions(false)
    setAutoCredentialsId(null)
    setAIConfig(null)
    setLoadingAIConfig(false)
    // NOTE: `projects` is intentionally NOT reset — it is global reference
    // data that persists between modal opens (was the same in original code).
  }, [])

  return {
    projects,
    loadingProjects,
    queriesRegions,
    loadingQueriesRegions,
    resolvedRegions,
    autoCredentialsId,
    aiConfig,
    loadingAIConfig,
    setAIConfig,
    loadProjects,
    loadQueriesRegions,
    loadActiveCredentials,
    loadAIConfig,
    resetData,
  }
}
